import React from 'react';
import { Mic, MessageCircle, Phone, Headphones, CheckCircle, Users } from 'lucide-react';
import { useSEO } from '../../hooks/useSEO';
import { Breadcrumb } from '../../components/Breadcrumb';
import { useBanner } from '../../contexts/BannerContext';

export const CognitiveVoicePage: React.FC = () => {
  useSEO('cognitive-voice' as any);
  const { isBannerVisible, bannerHeight } = useBanner();

  return (
    <>
      <Breadcrumb />
      <main className="pb-20">
      <section 
        className="px-4 sm:px-6 lg:px-8 mb-20"
        style={{ 
          paddingTop: isBannerVisible ? `${bannerHeight + 64 + 44 + 16}px` : '124px' // 64px header + 44px breadcrumb + 16px extra space
        }}
      >
        <div className="max-w-7xl mx-auto text-center">
          <div className="w-20 h-20 bg-gradient-to-r from-blue-600 to-blue-700 rounded-3xl mx-auto mb-8 flex items-center justify-center">
            <Mic className="w-10 h-10 text-white" />
          </div>
          <h1 
            className="text-5xl md:text-6xl font-bold mb-8 py-4 px-2 leading-loose bg-gradient-to-r from-blue-600 to-blue-700 bg-clip-text text-transparent overflow-visible min-h-fit"
            style={{ lineHeight: '1.8', minHeight: 'auto', overflow: 'visible', paddingTop: '16px', paddingBottom: '16px' }}
          >
            Cognitive Voice
          </h1>
          <p className="text-xl text-gray-700 dark:text-gray-300 max-w-4xl mx-auto leading-relaxed">
            Transform customer interactions with AI-powered voice solutions. 
            Deploy intelligent chatbots, virtual assistants, and voice analytics for seamless communication.
          </p>
        </div>
      </section>

      <div className="px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <section className="grid lg:grid-cols-2 gap-12 mb-16">
            <div className="bg-white/80 dark:bg-white/5 backdrop-blur-sm rounded-3xl border border-gray-200 dark:border-white/10 p-8">
              <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-6">Voice AI Solutions</h2>
              <div className="space-y-6">
                <div className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mr-3" />
                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-2">AI Chatbots & Virtual Assistants</h3>
                    <p className="text-gray-700 dark:text-gray-300">Intelligent conversational AI for customer support and engagement.</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mr-3" />
                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Speech-to-Text Automation</h3>
                    <p className="text-gray-700 dark:text-gray-300">Convert voice interactions to text with high accuracy and real-time processing.</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mr-3" />
                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Call Center Automation</h3>
                    <p className="text-gray-700 dark:text-gray-300">Automate customer service with intelligent call routing and response systems.</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mr-3" />
                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Interactive Voice Response (IVR)</h3>
                    <p className="text-gray-700 dark:text-gray-300">Smart IVR systems that understand natural language and intent.</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white/80 dark:bg-white/5 backdrop-blur-sm rounded-3xl border border-gray-200 dark:border-white/10 p-8">
              <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-6">Voice Capabilities</h2>
              <div className="grid grid-cols-2 gap-6">
                <div className="text-center">
                  <div className="w-16 h-16 bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl mx-auto mb-4 flex items-center justify-center">
                    <MessageCircle className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Natural Language</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-300">Human-like conversations</p>
                </div>
                <div className="text-center">
                  <div className="w-16 h-16 bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl mx-auto mb-4 flex items-center justify-center">
                    <Phone className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Multi-channel</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-300">Phone, web, mobile apps</p>
                </div>
                <div className="text-center">
                  <div className="w-16 h-16 bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl mx-auto mb-4 flex items-center justify-center">
                    <Headphones className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Real-time Analytics</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-300">Voice sentiment analysis</p>
                </div>
                <div className="text-center">
                  <div className="w-16 h-16 bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl mx-auto mb-4 flex items-center justify-center">
                    <Users className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Multilingual</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-300">Global language support</p>
                </div>
              </div>
            </div>
          </section>

          <section className="bg-white/80 dark:bg-white/5 backdrop-blur-sm rounded-3xl border border-gray-200 dark:border-white/10 p-8 mb-16">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-8 text-center">Use Cases</h2>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center">
                <h3 className="text-xl font-bold text-blue-600 dark:text-blue-400 mb-4">Customer Service</h3>
                <p className="text-gray-700 dark:text-gray-300">
                  24/7 AI-powered customer support, automated ticket resolution, and intelligent call routing for better service.
                </p>
              </div>
              <div className="text-center">
                <h3 className="text-xl font-bold text-blue-600 dark:text-blue-400 mb-4">Sales & Marketing</h3>
                <p className="text-gray-700 dark:text-gray-300">
                  Lead qualification, appointment scheduling, product recommendations, and personalized sales conversations.
                </p>
              </div>
              <div className="text-center">
                <h3 className="text-xl font-bold text-blue-600 dark:text-blue-400 mb-4">Internal Operations</h3>
                <p className="text-gray-700 dark:text-gray-300">
                  Voice-activated workflows, meeting transcription, voice commands for enterprise systems, and accessibility tools.
                </p>
              </div>
            </div>
          </section>

          <section className="bg-gradient-to-r from-blue-600/10 to-blue-700/10 rounded-3xl border border-blue-200 dark:border-blue-500/30 p-8 mb-16">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">Voice AI Benefits</h2>
              <p className="text-gray-700 dark:text-gray-300 text-lg">
                Voice interfaces designed to enhance enterprise customer interactions
              </p>
            </div>
            <div className="grid md:grid-cols-4 gap-6 text-center">
              <div>
                <div className="text-4xl font-bold text-blue-600 dark:text-blue-400 mb-2">Faster</div>
                <div className="text-sm text-gray-600 dark:text-gray-300">Resolution Times</div>
              </div>
              <div>
                <div className="text-4xl font-bold text-blue-600 dark:text-blue-400 mb-2">24/7</div>
                <div className="text-sm text-gray-600 dark:text-gray-300">Availability</div>
              </div>
              <div>
                <div className="text-4xl font-bold text-blue-600 dark:text-blue-400 mb-2">Reduced</div>
                <div className="text-sm text-gray-600 dark:text-gray-300">Costs</div>
              </div>
              <div>
                <div className="text-4xl font-bold text-blue-600 dark:text-blue-400 mb-2">Enhanced</div>
                <div className="text-sm text-gray-600 dark:text-gray-300">Experience</div>
              </div>
            </div>
          </section>

          <section className="bg-white/80 dark:bg-white/5 backdrop-blur-sm rounded-3xl border border-gray-200 dark:border-white/10 p-8 text-center">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-6">
              Give Your Business a Voice
            </h2>
            <p className="text-gray-700 dark:text-gray-300 text-lg mb-8 max-w-3xl mx-auto">
              Transform customer interactions with intelligent voice solutions. Create engaging, 
              natural conversations that delight customers and drive business growth.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => window.location.href = '/contact'}
                className="px-8 py-4 bg-gradient-to-r from-blue-600 to-blue-700 text-white font-semibold rounded-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300"
              >
                Start Speaking AI
              </button>
              <button
                onClick={() =>
                  window.open(
                    "https://outlook.office.com/book/CognitiveCraftHub2@cognitivecrafthub.com/?ismsaljsauthenabled",
                    "_blank"
                  )
                }
                className="px-8 py-4 border-2 border-gray-300 dark:border-white/30 text-gray-700 dark:text-white font-semibold rounded-lg hover:bg-gray-100 dark:hover:bg-white/10 hover:border-gray-400 dark:hover:border-white/50 transition-all duration-300"
              >
                Hear It in Action
              </button>
            </div>
          </section>
        </div>
      </div>
    </main>
    </>
  );
};